import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, ComposedChart, Tooltip, Legend, XAxis, YAxis,
  CartesianGrid
} from "recharts";
import {
  TrendingUp, TrendingDown, Loader2, Sparkles, RefreshCw, ChevronDown,
  AlertTriangle, Activity, Target as TargetIcon, IndianRupee, BarChart3,
  Layers, CalendarRange, Repeat
} from "lucide-react";

const COLORS = ["#0066CC", "#7C3AED", "#059669", "#D97706", "#DC2626", "#0891B2"];
const FY_COLORS = ["#0066CC", "#7C3AED", "#059669", "#D97706", "#DC2626", "#0891B2"];
const STREAM_LABELS = {
  focus_erp_license_saas: "Focus ERP License SaaS",
  erp_implementation: "ERP Implementation",
  app_development: "App Development",
  cloud_services: "Cloud Services",
  amc_erp: "AMC - ERP",
  amc_web: "AMC - Web",
  absolin_products: "ABSOLIN Products",
  ai_consulting_services: "AI Consulting Services",
  others: "Others",
  // Legacy keys (backwards compat for old data)
  project_deliverables: "Project Deliverables (legacy)",
  license_revenue: "License Revenue (legacy)",
  amc: "AMC (legacy)",
};

const fmt = (amt) => {
  if (!amt && amt !== 0) return "0";
  const lakhs = amt / 100000;
  if (Math.abs(lakhs) >= 100) return `${Math.round(lakhs).toLocaleString("en-IN")} L`;
  if (Math.abs(lakhs) >= 10) return `${lakhs.toFixed(1)} L`;
  return `${lakhs.toFixed(2)} L`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2.5 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: ₹{fmt(p.value)}</p>
      ))}
    </div>
  );
};

function KpiCard({ label, value, sub, color, icon: Icon, trend }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
            {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
          </div>
          {Icon && (
            <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${color}15` }}>
              <Icon className="w-4 h-4" style={{ color }} />
            </div>
          )}
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1 mt-2">
            {trend >= 0 ? <TrendingUp className="w-3 h-3 text-emerald-600" /> : <TrendingDown className="w-3 h-3 text-red-600" />}
            <span className={`text-[10px] font-medium ${trend >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {trend >= 0 ? "+" : ""}{trend}% trend
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function CollectionAnalytics({ analytics, fyComparison, yoyComparison, revRec, selectedFys = [], fyTargets = [], loading, loadingAi, aiInsights, onRefresh, onGenerateAi, onFysChange }) {
  const trendWithForecast = useMemo(() => {
    if (!analytics) return [];
    const past = analytics.monthly_trend.map(m => ({ ...m, type: "actual" }));
    const future = analytics.forecast.map(f => ({ label: f.month, collected: null, predicted: f.predicted, target: past[0]?.target || 0, type: "forecast" }));
    // Connect actual → forecast by overlaying predicted on the last actual point
    if (past.length) past[past.length - 1].predicted = past[past.length - 1].collected;
    return [...past, ...future];
  }, [analytics]);

  const streamData = useMemo(() => {
    if (!analytics) return [];
    return Object.entries(analytics.stream_mix || {}).map(([k, v]) => ({
      name: STREAM_LABELS[k] || k, value: v.total, count: v.count
    }));
  }, [analytics]);

  const projectAgingData = useMemo(() => {
    if (!analytics?.project_aging) return [];
    const palette = {
      "In Grace (<5 mo)": "#3B82F6",
      "0-3 mo overdue":   "#10B981",
      "3-6 mo overdue":   "#F59E0B",
      "6-12 mo overdue":  "#EF4444",
      "12+ mo overdue":   "#7C2D12",
    };
    return analytics.project_aging.map(b => ({
      bucket: b.bucket,
      value: b.outstanding || 0,
      count: b.count || 0,
      projects: b.projects || [],
      color: palette[b.bucket] || "#94A3B8",
    }));
  }, [analytics]);

  // FY comparison chart data — pivot by month index (Apr=0..Mar=11)
  // MUST be declared before any early-returns to comply with rules-of-hooks
  const comparisonChartData = useMemo(() => {
    if (!fyComparison || !fyComparison.comparison?.length) return [];
    const months = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
    return months.map((label, idx) => {
      const row = { month: label };
      fyComparison.comparison.forEach(fy => {
        row[fy.fy_label] = fy.monthly_data[idx]?.collected || 0;
      });
      return row;
    });
  }, [fyComparison]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!analytics) return <div className="text-center text-slate-400 py-12">No analytics data available.</div>;

  const s = analytics.summary || {};
  const efficiency = s.ytd_efficiency || 0;
  const ytdCollected = s.total_collected_ytd || 0;
  const ytdTarget = s.total_target_ytd || 0;
  const currentFy = analytics.current_fy || {};

  // Derive list of selectable FY years — combine user-defined FYs + last 5 years
  const currentYear = new Date().getFullYear();
  const currentFyYear = new Date().getMonth() >= 3 ? currentYear : currentYear - 1;
  const fyYearSet = new Set([
    ...(fyTargets.map(f => f.fy_start_year) || []),
    currentFyYear, currentFyYear - 1, currentFyYear - 2,
  ]);
  const availableFys = Array.from(fyYearSet).sort((a, b) => b - a);
  const fyLabel = (y) => `FY ${y}-${String(y + 1).slice(-2)}`;

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Collection Analytics</h1>
            {currentFy.label && (
              <span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-semibold" data-testid="current-fy-badge">
                {currentFy.label}{currentFy.source === "default" ? " · default targets" : ""}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">Predictive intelligence over your historical collections</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* FY selector */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="fy-selector-btn">
                <BarChart3 className="w-3.5 h-3.5" />
                {selectedFys.length === 0 ? "Compare FY" : `${selectedFys.length} FY selected`}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              <div className="flex items-center justify-between px-2 py-1.5">
                <p className="text-xs font-semibold text-slate-600">Select FY for comparison</p>
                {selectedFys.length > 0 && (
                  <button onClick={() => onFysChange([])} className="text-[10px] text-blue-600 hover:underline">Clear</button>
                )}
              </div>
              <p className="text-[10px] text-slate-500 px-2 pb-1">Pick one or multiple FYs (max 6)</p>
              <div className="max-h-64 overflow-y-auto space-y-0.5 mt-1">
                {availableFys.map((y, i) => (
                  <label key={y} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer text-sm">
                    <Checkbox
                      checked={selectedFys.includes(y)}
                      onCheckedChange={(checked) => {
                        let next;
                        if (checked && selectedFys.length >= 6) return;
                        if (checked) next = [...selectedFys, y].sort((a, b) => b - a);
                        else next = selectedFys.filter(v => v !== y);
                        onFysChange(next);
                      }}
                      data-testid={`fy-option-${y}`}
                    />
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: FY_COLORS[i % FY_COLORS.length] }} />
                    <span className="flex-1">{fyLabel(y)}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="sm" onClick={onRefresh} className="gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</Button>
          <Button onClick={onGenerateAi} disabled={loadingAi} className="bg-gradient-to-r from-[#0066CC] to-[#7C3AED] hover:opacity-90 gap-2 text-white" data-testid="ai-insights-btn">
            {loadingAi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI Insights
          </Button>
        </div>
      </div>

      {/* YoY Performance Pulse — 4 windows comparison */}
      {yoyComparison && (
        <Card className="border-2 border-blue-100 bg-gradient-to-br from-white to-blue-50/30" data-testid="yoy-comparison-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#0066CC]" />
                  Performance Pulse — Year-on-Year Comparison
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5">Same-window comparison vs last year (apples-to-apples). Track team velocity & market trend.</p>
              </div>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider">As on {yoyComparison.as_on}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {[
                { key: "month",   icon: "🗓️", title: "Month-on-Month",  hint: "MTD vs same period last year" },
                { key: "quarter", icon: "📊", title: "Quarter-on-Quarter", hint: "QTD vs same period last year" },
                { key: "week",    icon: "📈", title: "Week-on-Week",    hint: "WTD vs same ISO week last year" },
                { key: "fy",      icon: "🎯", title: "FY-on-FY",        hint: "YTD vs last FY same days" },
              ].map(({ key, icon, title, hint }) => {
                const b = yoyComparison[key];
                if (!b) return null;
                const isUp = b.trend === "up";
                const isDown = b.trend === "down";
                return (
                  <div key={key} className="rounded-xl border bg-white p-3.5 shadow-sm" data-testid={`yoy-card-${key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                          <span className="mr-1">{icon}</span>{title}
                        </p>
                        <p className="text-[11px] text-slate-700 font-semibold mt-0.5">{b.label}</p>
                      </div>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isUp ? "bg-emerald-100 text-emerald-700" : isDown ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                        {isUp && <TrendingUp className="w-3 h-3 inline mr-0.5" />}
                        {isDown && <TrendingDown className="w-3 h-3 inline mr-0.5" />}
                        {b.growth_pct >= 0 ? "+" : ""}{b.growth_pct}%
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 italic mb-2">{b.note}</p>
                    {/* Current */}
                    <div className="flex items-baseline gap-2 mb-1.5">
                      <p className={`text-2xl font-bold ${isUp ? "text-emerald-600" : isDown ? "text-red-600" : "text-slate-800"}`}>₹{fmt(b.current.collected)}</p>
                      <p className="text-[10px] text-slate-500">current</p>
                    </div>
                    <div className="flex items-baseline gap-2 mb-2">
                      <p className="text-sm text-slate-500">₹{fmt(b.prior.collected)}</p>
                      <p className="text-[10px] text-slate-400">prior year</p>
                    </div>
                    {/* Δ + meta */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-[10px]">
                      <span className="text-slate-500">
                        Δ <span className={`font-semibold ${b.delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>{b.delta >= 0 ? "+" : ""}₹{fmt(b.delta)}</span>
                      </span>
                      <span className="text-slate-400">{b.current.receipts} rcpts · {b.current.projects} proj</span>
                    </div>
                    {/* FY target context */}
                    {key === "fy" && b.current.target_to_date > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        <div className="flex justify-between text-[10px] mb-1">
                          <span className="text-slate-500">Target to-date · ₹{fmt(b.current.target_to_date)}</span>
                          <span className={`font-bold ${b.current.target_achievement_pct >= 100 ? "text-emerald-700" : b.current.target_achievement_pct >= 70 ? "text-amber-700" : "text-red-700"}`}>{b.current.target_achievement_pct}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${b.current.target_achievement_pct >= 100 ? "bg-emerald-500" : b.current.target_achievement_pct >= 70 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${Math.min(100, b.current.target_achievement_pct)}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Insight strip */}
            {(() => {
              const blocks = ["month", "quarter", "week", "fy"].map(k => yoyComparison[k]).filter(Boolean);
              const upCount = blocks.filter(b => b.trend === "up").length;
              const downCount = blocks.filter(b => b.trend === "down").length;
              let insight = "";
              if (upCount === 4) insight = "🟢 All four windows show YoY growth — sustained momentum across short-term & long-term.";
              else if (downCount === 4) insight = "🔴 Every window is below last year — review pipeline urgency & top accounts immediately.";
              else if (upCount === 3) insight = "🟡 3/4 windows growing. Identify the lagging window and dig into root cause.";
              else if (upCount === 2 && downCount === 2) insight = "⚖️ Mixed signals — short-term may differ from long-term. Watch the FY trend, it sets the year.";
              else if (downCount === 3) insight = "🟠 3/4 windows declining — possible market slowdown or focus issue. Strategy review recommended.";
              else insight = "📊 Mixed signals across windows — review individual cards for context.";
              return <p className="mt-4 text-[11px] text-slate-600 italic bg-slate-50 rounded-lg p-2.5 border border-slate-100">{insight}</p>;
            })()}
          </CardContent>
        </Card>
      )}

      {/* FY Comparison Chart */}
      {fyComparison && comparisonChartData.length > 0 && (
        <Card data-testid="fy-comparison-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap mb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-sm">FY Side-by-Side Comparison</h3>
                <p className="text-[11px] text-slate-500">Monthly collection across selected financial years</p>
              </div>
              <div className="flex flex-wrap gap-3 text-[10px]">
                {fyComparison.comparison.map((fy, i) => (
                  <span key={fy.fy_label} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: FY_COLORS[i % FY_COLORS.length] }} />
                    <strong>{fy.fy_label}</strong>
                    <span className="text-slate-500">— ₹{fmt(fy.total_collected)} ({fy.achievement_pct}%)</span>
                  </span>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={comparisonChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {fyComparison.comparison.map((fy, i) => (
                  <Bar key={fy.fy_label} dataKey={fy.fy_label} fill={FY_COLORS[i % FY_COLORS.length]} radius={[4, 4, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label={`${currentFy.label || "FY"} Collected`} value={`₹${fmt(ytdCollected)}`} sub={`vs YTD target ₹${fmt(currentFy.ytd_target || ytdTarget)}`} color="#0066CC" icon={IndianRupee} />
        <KpiCard label="YTD Efficiency" value={`${efficiency}%`} sub={`${currentFy.days_elapsed || 0}/${currentFy.days_in_year || 365} days · Annual: ${currentFy.achievement_pct || 0}%`} color={efficiency >= 90 ? "#059669" : efficiency >= 70 ? "#D97706" : "#DC2626"} icon={TargetIcon} />
        <KpiCard label="Total Outstanding" value={`₹${fmt(s.total_outstanding)}`} sub={`${s.active_projects || 0} active projects`} color="#DC2626" icon={AlertTriangle} />
        <KpiCard label="Avg Monthly" value={`₹${fmt(s.avg_monthly)}`} sub={`Best: ${s.best_month?.label || "-"}`} color="#7C3AED" icon={Activity} trend={analytics.trend_slope > 0 ? Math.round((analytics.trend_slope / Math.max(s.avg_monthly, 1)) * 100) : Math.round((analytics.trend_slope / Math.max(s.avg_monthly, 1)) * 100)} />
      </div>

      {/* AI Insights Panel */}
      {(aiInsights || loadingAi) && (
        <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-blue-50">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-violet-600" />
              <h3 className="font-bold text-slate-900 text-sm">AI Strategic Analysis</h3>
              <span className="text-[10px] uppercase tracking-wider bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-semibold">CFO View</span>
            </div>
            {loadingAi ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Analyzing patterns and generating insights...
              </div>
            ) : (
              <div className="prose prose-sm max-w-none text-slate-700 text-sm whitespace-pre-wrap" data-testid="ai-insights-content">{aiInsights}</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Monthly Trend with Forecast */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold text-slate-900 text-sm">Monthly Collection Trend & 3-Month Forecast</h3>
              <p className="text-[11px] text-slate-500">Forecast uses Simple Moving Average + linear trend</p>
            </div>
            <div className="flex gap-3 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0066CC]"></span> Actual</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#7C3AED]"></span> Forecast</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300"></span> Target</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={trendWithForecast}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="collected" name="Actual Collected" fill="#0066CC" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="predicted" name="Forecast" stroke="#7C3AED" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4, fill: "#7C3AED" }} />
              <Line type="monotone" dataKey="target" name="Target" stroke="#94A3B8" strokeWidth={1} strokeDasharray="2 2" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Forecast cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {analytics.forecast.map((f, i) => (
          <Card key={f.month} className="border-violet-200 bg-violet-50/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase font-semibold text-violet-600">Forecast • {f.month}</p>
                <span className="text-[10px] text-slate-400">+{i + 1}mo</span>
              </div>
              <p className="text-2xl font-bold text-violet-700 mt-1">₹{fmt(f.predicted)}</p>
              <p className="text-[10px] text-slate-500 mt-1">SMA-3: ₹{fmt(f.sma_3)} • SMA-6: ₹{fmt(f.sma_6)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Two-column: Stream Mix + Aging */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5">
            <h3 className="font-bold text-slate-900 text-sm mb-3">Revenue Stream Mix</h3>
            {streamData.length === 0 ? <p className="text-xs text-slate-400 py-8 text-center">No receipts recorded yet</p> : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={streamData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}>
                    {streamData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `₹${fmt(v)}`} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card data-testid="project-aging-card">
          <CardContent className="p-5">
            <h3 className="font-bold text-slate-900 text-sm mb-1">Project Aging Analysis</h3>
            <p className="text-[11px] text-slate-500 mb-3">Aging measured from each project's start date + 5 months (minimum collection point). Pending = expected billed to date − collected.</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={projectAgingData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
                <YAxis dataKey="bucket" type="category" tick={{ fontSize: 10 }} width={130} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2.5 text-xs max-w-[280px]">
                      <p className="font-semibold text-slate-700 mb-1">{d.bucket}</p>
                      <p className="text-slate-600">{d.count} project{d.count === 1 ? "" : "s"} · Pending ₹{fmt(d.value)}</p>
                      {d.projects.slice(0, 4).map((p, i) => (
                        <p key={i} className="text-[10.5px] text-slate-500 mt-0.5">• {p.name}: ₹{fmt(p.pending)}{p.in_grace ? "" : ` · ${p.months_overdue}mo`}</p>
                      ))}
                      {d.projects.length > 4 && <p className="text-[10px] text-slate-400 mt-0.5">+{d.projects.length - 4} more</p>}
                    </div>
                  );
                }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {projectAgingData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Trend */}
      <Card>
        <CardContent className="p-5">
          <h3 className="font-bold text-slate-900 text-sm mb-3">Weekly Collection Trend (last 12 weeks)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={analytics.weekly_trend}>
              <defs>
                <linearGradient id="weeklyGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0066CC" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#0066CC" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="collected" name="Collected" stroke="#0066CC" strokeWidth={2} fill="url(#weeklyGrad)" />
              <Line type="monotone" dataKey="target" name="Target" stroke="#94A3B8" strokeDasharray="2 2" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* ===== Revenue Recognition (NetSuite ARM-style) ===== */}
      {revRec && (
        <RevenueRecognitionDashlet data={revRec} />
      )}

      {/* Velocity Table */}
      {analytics.velocity?.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h3 className="font-bold text-slate-900 text-sm mb-3">Collection Velocity (avg days between receipts)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2 font-semibold text-slate-600">Project</th>
                    <th className="text-right p-2 font-semibold text-slate-600">Avg Gap</th>
                    <th className="text-right p-2 font-semibold text-slate-600">Receipts</th>
                    <th className="text-right p-2 font-semibold text-slate-600">Collected</th>
                  </tr>
                </thead>
                <tbody>
                  {[...analytics.velocity].sort((a, b) => a.avg_days - b.avg_days).map((v, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-2 font-medium text-slate-800">{v.name}</td>
                      <td className="p-2 text-right">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${v.avg_days <= 15 ? "bg-emerald-100 text-emerald-700" : v.avg_days <= 30 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                          {v.avg_days} days
                        </span>
                      </td>
                      <td className="p-2 text-right text-slate-600">{v.receipts}</td>
                      <td className="p-2 text-right font-semibold text-emerald-600">₹{fmt(v.collected)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ================================================================
   Revenue Recognition Dashlet — NetSuite ARM-style
   =============================================================== */

const FREQ_COLOR = {
  one_time:  "#0891B2",
  monthly:   "#0066CC",
  quarterly: "#7C3AED",
  annually:  "#059669",
};
const FREQ_LABEL = { one_time: "One Time", monthly: "Monthly", quarterly: "Quarterly", annually: "Annually" };

function RevRecKpi({ label, value, sub, tone = "blue", icon: Icon }) {
  const map = {
    blue: "text-[#0066CC] bg-blue-50",
    green: "text-emerald-600 bg-emerald-50",
    amber: "text-amber-600 bg-amber-50",
    red: "text-red-600 bg-red-50",
    violet: "text-violet-600 bg-violet-50",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3">
      <div className={`p-2 rounded-lg ${map[tone]}`}>
        {Icon && <Icon className="w-4 h-4" />}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
        <p className="text-xl font-bold text-slate-900 mt-0.5 truncate">{value}</p>
        {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function RevenueRecognitionDashlet({ data }) {
  const [freqFilter, setFreqFilter] = useState("all");
  const [sched, setSched] = useState({ page: 1, pageSize: 10 });

  const s = data.summary || {};
  const filtered = useMemo(() => {
    if (freqFilter === "all") return data.schedule_by_project || [];
    return (data.schedule_by_project || []).filter(r => r.frequency === freqFilter);
  }, [freqFilter, data.schedule_by_project]);

  const paginated = filtered.slice((sched.page - 1) * sched.pageSize, sched.page * sched.pageSize);
  const totalPages = Math.max(1, Math.ceil(filtered.length / sched.pageSize));

  const waterfallChart = (data.schedule_12mo || []).map(w => ({
    month: w.month_label,
    recognized: Math.round(w.recognized),
    projected: Math.round(w.projected),
    is_current: w.is_current,
  }));

  const byFreqChart = (data.by_frequency || []).map(f => ({
    name: f.label,
    key: f.key,
    Recognized: Math.round(f.recognized),
    Remaining: Math.round(f.remaining),
    active_lines: f.active_lines,
  }));

  return (
    <Card className="border-2 border-violet-100 bg-gradient-to-br from-white to-violet-50/40" data-testid="revrec-dashlet">
      <CardContent className="p-5 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
              <Layers className="w-4 h-4 text-violet-600" />
              Revenue Recognition Overview
              <span className="text-[10px] uppercase tracking-wider bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-semibold">ARM · ASC 606 style</span>
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Contract-based revenue view driven by billing lines. Recognized = billed to-date · Deferred = billed but not collected · MRR/ARR from recurring lines.
            </p>
          </div>
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">As on {data.as_on}</span>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="revrec-kpis">
          <RevRecKpi label="Recognized (to-date)" value={`₹${fmt(s.recognized_ytd)}`} sub={`${s.active_lines || 0} billing lines · ${s.active_projects || 0} contracts`} tone="blue" icon={TargetIcon} />
          <RevRecKpi label="Collected" value={`₹${fmt(s.collected)}`} sub={`Deferred: ₹${fmt(s.deferred_revenue)}`} tone="green" icon={IndianRupee} />
          <RevRecKpi label="MRR" value={`₹${fmt(s.mrr)}`} sub={`Monthly recurring (all freqs normalized)`} tone="violet" icon={Repeat} />
          <RevRecKpi label="ARR" value={`₹${fmt(s.arr)}`} sub={`Annualized run-rate`} tone="amber" icon={CalendarRange} />
        </div>

        {/* Waterfall — 12 month recognition schedule */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div>
              <h4 className="text-sm font-semibold text-slate-800">Recognition Waterfall — 12 Months</h4>
              <p className="text-[11px] text-slate-500">Past 5 · Current · Next 6 months. Recognized (fired) vs Projected (scheduled)</p>
            </div>
            <div className="flex gap-3 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#0066CC]" /> Recognized</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-violet-400" /> Projected</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={waterfallChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="Recognized" stackId="a" fill="#0066CC" radius={[0, 0, 0, 0]}>
                {waterfallChart.map((entry, i) => (
                  <Cell key={i} fill={entry.is_current ? "#059669" : "#0066CC"} />
                ))}
              </Bar>
              <Bar dataKey="Projected" stackId="a" fill="#A78BFA" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* By-frequency split */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4 lg:col-span-2" data-testid="revrec-freq-chart">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="text-sm font-semibold text-slate-800">Revenue by Billing Frequency</h4>
                <p className="text-[11px] text-slate-500">Recognized to-date + Remaining over next 12 months</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={byFreqChart} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={70} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="Recognized" stackId="f" fill="#0066CC" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Remaining"  stackId="f" fill="#CBD5E1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h4 className="text-sm font-semibold text-slate-800 mb-2">Contract Mix</h4>
            <div className="space-y-2">
              {byFreqChart.map(row => {
                const total = row.Recognized + row.Remaining;
                const pct = total > 0 ? Math.min(100, Math.round((row.Recognized / total) * 100)) : 0;
                return (
                  <div key={row.key} className="text-xs">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: FREQ_COLOR[row.key] }} />
                        <span className="font-medium text-slate-700">{row.name}</span>
                        <span className="text-[10px] text-slate-400">· {row.active_lines} line{row.active_lines !== 1 ? "s" : ""}</span>
                      </div>
                      <span className="font-semibold text-slate-800 tabular-nums">₹{fmt(total)}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: FREQ_COLOR[row.key] }} />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5">{pct}% recognized</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Schedule Table */}
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between flex-wrap gap-2 p-4 border-b border-slate-100">
            <div>
              <h4 className="text-sm font-semibold text-slate-800">Revenue Recognition Schedule</h4>
              <p className="text-[11px] text-slate-500">Per billing line — sorted by remaining revenue</p>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="revrec-freq-tabs">
              {["all", "one_time", "monthly", "quarterly", "annually"].map(f => (
                <button
                  key={f}
                  data-testid={`revrec-freq-${f}`}
                  onClick={() => { setFreqFilter(f); setSched(s => ({ ...s, page: 1 })); }}
                  className={`px-2.5 py-1 text-[11px] rounded-full border transition ${freqFilter === f ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                >
                  {f === "all" ? "All" : FREQ_LABEL[f]}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="revrec-schedule-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Contract / Project</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Type</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Frequency</th>
                  <th className="text-right p-2.5 font-semibold text-slate-600">Amount</th>
                  <th className="text-right p-2.5 font-semibold text-slate-600 hidden md:table-cell">Periods</th>
                  <th className="text-right p-2.5 font-semibold text-slate-600">Recognized</th>
                  <th className="text-right p-2.5 font-semibold text-slate-600">Collected</th>
                  <th className="text-right p-2.5 font-semibold text-slate-600">Deferred</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600 hidden lg:table-cell">Next Bill</th>
                  <th className="text-left p-2.5 font-semibold text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr><td colSpan={10} className="p-6 text-center text-slate-400 italic">No matching billing lines</td></tr>
                ) : paginated.map((row, i) => (
                  <tr key={row.billing_line_id || i} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-2.5 font-medium text-slate-800 max-w-[220px] truncate">{row.project_name}</td>
                    <td className="p-2.5"><span className="text-[10px] font-semibold bg-blue-50 text-[#0066CC] px-1.5 py-0.5 rounded">{row.type}</span></td>
                    <td className="p-2.5"><span className="text-[10px] font-semibold" style={{ color: FREQ_COLOR[row.frequency] }}>{FREQ_LABEL[row.frequency]}</span></td>
                    <td className="p-2.5 text-right text-slate-700 tabular-nums">₹{fmt(row.amount)}</td>
                    <td className="p-2.5 text-right text-slate-500 hidden md:table-cell">{row.completed_periods}</td>
                    <td className="p-2.5 text-right font-semibold text-[#0066CC] tabular-nums">₹{fmt(row.recognized)}</td>
                    <td className="p-2.5 text-right font-semibold text-emerald-600 tabular-nums">₹{fmt(row.collected)}</td>
                    <td className={`p-2.5 text-right font-semibold tabular-nums ${row.outstanding > 0 ? "text-red-600" : "text-slate-400"}`}>₹{fmt(row.outstanding)}</td>
                    <td className="p-2.5 text-slate-500 text-[11px] hidden lg:table-cell">{row.next_bill_date || "—"}</td>
                    <td className="p-2.5">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${row.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length > sched.pageSize && (
            <div className="flex items-center justify-between p-3 border-t border-slate-100 text-[11px]">
              <span className="text-slate-500">Showing {(sched.page - 1) * sched.pageSize + 1}–{Math.min(sched.page * sched.pageSize, filtered.length)} of {filtered.length}</span>
              <div className="flex gap-1">
                <button disabled={sched.page === 1} onClick={() => setSched(s => ({ ...s, page: s.page - 1 }))} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">Prev</button>
                <span className="px-2 py-1 text-slate-600">{sched.page} / {totalPages}</span>
                <button disabled={sched.page >= totalPages} onClick={() => setSched(s => ({ ...s, page: s.page + 1 }))} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50">Next</button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

